// src/pages/SMSCommunication.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  InputAdornment,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Send as SendIcon,
  Group as GroupIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  CreditScore as CreditsIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  SignalCellularAlt as SignalIcon,
  SimCard as SimIcon,
  Speed as SpeedIcon,
  CheckCircleOutline as DeliveredIcon,
  ErrorOutline as FailedIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, addDoc, query, orderBy, limit, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const SMSCommunication = () => {
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedYearGroup, setSelectedYearGroup] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [yearGroups, setYearGroups] = useState(new Map());
  const [message, setMessage] = useState('');
  const [messageLength, setMessageLength] = useState(0);
  const [messageParts, setMessageParts] = useState(1);
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [sendingHistory, setSendingHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSMS, setPendingSMS] = useState(null);
  const [showPrivateToken, setShowPrivateToken] = useState(false);
  const [smsSettings, setSmsSettings] = useState({
    gatewayUrl: '',
    privateToken: '',
    senderId: '',
    maxPerBatch: 20,
    enableDeliveryReport: true,
    retryCount: 3,
    timeoutSeconds: 30
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Helper function to get year group prefix
  const getYearGroupPrefix = (yearName) => {
    if (!yearName || yearName.trim() === '') return 'not';
    const prefix = yearName.substring(0, 3).toLowerCase();
    return prefix === '' ? 'not' : prefix;
  };

  // Calculate SMS message parts
  const calculateMessageParts = (text) => {
    const length = text.length;
    if (length === 0) return { length: 0, parts: 1 };
    
    // GSM 7-bit characters
    const gsmChars = /^[A-Za-z0-9\s\.,\?!'\"@\(\)\+\-=\/\*\#\$%&£€¥§°@©®™\[\]\{\}\^~\\|<>_¨áàâäãåæçéèêëíìîïñóòôöõøúùûüß]+$/;
    const hasUnicode = !gsmChars.test(text);
    const charsPerPart = hasUnicode ? 70 : 160;
    const parts = Math.ceil(length / charsPerPart);
    
    return { length, parts, hasUnicode, charsPerPart };
  };

  // Handle message change
  const handleMessageChange = (e) => {
    const text = e.target.value;
    setMessage(text);
    const { length, parts } = calculateMessageParts(text);
    setMessageLength(length);
    setMessageParts(parts);
  };

  // Fetch gateway status periodically
  useEffect(() => {
    fetchGatewayStatus();
    const interval = setInterval(fetchGatewayStatus, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [smsSettings.gatewayUrl, smsSettings.privateToken]);

  // Fetch data on mount
  useEffect(() => {
    fetchUsers();
    fetchSmsSettings();
    fetchSendingHistory();
  }, []);

  // Filter users when selected year group changes
  useEffect(() => {
    filterUsersByYearGroup();
  }, [selectedYearGroup, users]);

  const fetchGatewayStatus = async () => {
    if (!smsSettings.gatewayUrl) {
      setGatewayStatus(null);
      return;
    }

    try {
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authentication if private token exists
      if (smsSettings.privateToken) {
        headers['Authorization'] = `Bearer ${smsSettings.privateToken}`;
        headers['X-API-Key'] = smsSettings.privateToken;
      }

      const response = await fetch(`${smsSettings.gatewayUrl}/status`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle different response formats from various gateway APKs
      if (result.success || result.status === 'success' || result.connected) {
        setGatewayStatus({
          connected: true,
          simNumber: result.sim_number || result.sim || result.phoneNumber || 'Unknown',
          signalStrength: result.signal || result.signal_strength || result.signalStrength || 75,
          balance: result.balance || result.sms_balance || result.credits || 0,
          deviceId: result.deviceId || result.device_id || 'N/A'
        });
      } else {
        setGatewayStatus({
          connected: false,
          simNumber: 'N/A',
          signalStrength: 0,
          balance: 0,
          deviceId: 'N/A'
        });
      }
    } catch (error) {
      console.error('Error fetching gateway status:', error);
      setGatewayStatus({
        connected: false,
        simNumber: 'N/A',
        signalStrength: 0,
        balance: 0,
        deviceId: 'N/A'
      });
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersList);
      
      // Group users by year group prefix
      const groups = new Map();
      
      usersList.forEach(user => {
        const yearName = user.yearName || 'Not specified';
        const prefix = getYearGroupPrefix(yearName);
        
        const userData = {
          id: user.id,
          name: user.fullName || user.firstName || user.email,
          phone: user.phone,
          email: user.email,
          yearName: yearName
        };
        
        if (!groups.has(prefix)) {
          groups.set(prefix, {
            displayName: yearName,
            prefix: prefix,
            members: [],
            count: 0
          });
        }
        
        const group = groups.get(prefix);
        group.members.push(userData);
        group.count++;
      });
      
      setYearGroups(groups);
      
      // Auto-select all users initially
      setFilteredUsers(usersList);
      setSelectedUsers(usersList.map(user => user.id));
      
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsSettings = async () => {
    try {
      const settingsRef = collection(db, 'settings');
      const settingsDoc = await getDocs(query(settingsRef));
      settingsDoc.forEach(doc => {
        if (doc.id === 'smsSettings') {
          setSmsSettings(doc.data());
        }
      });
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
    }
  };

  const fetchSendingHistory = async () => {
    try {
      const historyRef = collection(db, 'smsHistory');
      const q = query(historyRef, orderBy('createdAt', 'desc'), limit(50));
      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSendingHistory(history);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const filterUsersByYearGroup = () => {
    if (selectedYearGroup === 'all') {
      setFilteredUsers(users);
      setSelectedUsers(users.map(user => user.id));
    } else {
      const group = yearGroups.get(selectedYearGroup);
      if (group) {
        const filtered = group.members.map(member => {
          return users.find(user => user.id === member.id);
        }).filter(Boolean);
        
        setFilteredUsers(filtered);
        setSelectedUsers(filtered.map(user => user.id));
      } else {
        setFilteredUsers([]);
        setSelectedUsers([]);
      }
    }
  };

  const handleYearGroupChange = (event) => {
    const prefix = event.target.value;
    setSelectedYearGroup(prefix);
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleUserToggle = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const validatePhoneNumber = (phone) => {
    if (!phone || phone === 'N/A') return false;
    // Kenyan phone number validation
    const phoneRegex = /^(?:\+254|0|254)?[17]\d{8}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // Format for Kenya (254)
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.substring(1);
    } else if (!cleaned.startsWith('254') && cleaned.length === 9) {
      cleaned = '254' + cleaned;
    }
    
    return cleaned;
  };

  const prepareSMSBatch = () => {
    const selectedUsersDetails = users.filter(user => selectedUsers.includes(user.id));
    const validRecipients = selectedUsersDetails
      .filter(user => validatePhoneNumber(user.phone))
      .map(user => ({
        id: user.id,
        phone: formatPhoneNumber(user.phone),
        name: user.name
      }));
    
    if (validRecipients.length === 0) {
      showSnackbar('No valid phone numbers found in selected recipients', 'error');
      return null;
    }
    
    // Calculate estimated cost (adjust based on your bundle rates)
    const estimatedCost = (validRecipients.length * messageParts) * 0.50; // KES 0.50 per part
    
    const smsData = {
      recipients: validRecipients,
      message: message,
      messageParts: messageParts,
      totalRecipients: validRecipients.length,
      estimatedCost: estimatedCost,
      scheduledFor: isScheduled ? `${scheduleDate}T${scheduleTime}` : null
    };
    
    setPendingSMS(smsData);
    setShowConfirmDialog(true);
  };

  const sendSMS = async () => {
    if (!pendingSMS) return;
    
    setShowConfirmDialog(false);
    setLoading(true);
    
    try {
      // Create SMS batch record in Firestore
      const batchRef = await addDoc(collection(db, 'smsBatches'), {
        message: message,
        totalRecipients: pendingSMS.totalRecipients,
        messageParts: messageParts,
        status: 'processing',
        scheduledFor: pendingSMS.scheduledFor || null,
        createdAt: Timestamp.now(),
        createdBy: 'admin'
      });
      
      // Prepare headers with authentication
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add private token authentication if available
      if (smsSettings.privateToken) {
        headers['Authorization'] = `Bearer ${smsSettings.privateToken}`;
        headers['X-API-Key'] = smsSettings.privateToken;
      }

      // Prepare recipients - some gateways expect comma-separated or array
      const recipients = pendingSMS.recipients.map(r => r.phone);
      
      // Different gateway APKs expect different payload formats
      // Try the most common formats
      let payload;
      let endpoint;

      // Check if using token in body (common in some APKs)
      if (smsSettings.privateToken && !smsSettings.gatewayUrl.includes('token=')) {
        // Format 1: Token in headers (Bearer/API Key)
        payload = {
          to: recipients,
          message: message,
          sender: smsSettings.senderId || 'WhiteDoves',
          batchId: batchRef.id
        };
        endpoint = `${smsSettings.gatewayUrl}/api/send-batch`;
      } else {
        // Format 2: Token in body (alternative format)
        payload = {
          token: smsSettings.privateToken,
          phone: recipients,
          message: message,
          sender: smsSettings.senderId || 'WhiteDoves',
          batchId: batchRef.id
        };
        endpoint = `${smsSettings.gatewayUrl}/send`;
      }

      // Send to Android SMS Gateway
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle different response formats
      const isSuccess = result.success || result.status === 'success' || result.sent === true;
      
      if (isSuccess) {
        // Extract delivery counts from different response formats
        const delivered = result.delivered || result.success_count || result.sent || result.sentCount || 0;
        const failed = result.failed || result.failed_count || result.errorCount || 0;
        
        // Update batch status
        await updateDoc(doc(db, 'smsBatches', batchRef.id), {
          status: 'completed',
          gatewayResponse: result,
          completedAt: Timestamp.now(),
          deliveredCount: delivered,
          failedCount: failed
        });
        
        // Record in history
        await addDoc(collection(db, 'smsHistory'), {
          batchId: batchRef.id,
          message: message,
          recipientCount: pendingSMS.totalRecipients,
          deliveredCount: delivered,
          failedCount: failed,
          messageParts: messageParts,
          createdAt: Timestamp.now()
        });
        
        showSnackbar(
          `✅ SMS sent! Delivered: ${delivered}, Failed: ${failed}`,
          'success'
        );
        
        // Clear form
        setMessage('');
        setMessageLength(0);
        setMessageParts(1);
        setIsScheduled(false);
        setScheduleDate('');
        setScheduleTime('');
        
        // Refresh history
        fetchSendingHistory();
      } else {
        throw new Error(result.error || result.message || 'Gateway returned error');
      }
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      showSnackbar(`Failed to send SMS: ${error.message}`, 'error');
      
      // Update batch as failed
      if (pendingSMS.batchId) {
        await updateDoc(doc(db, 'smsBatches', pendingSMS.batchId), {
          status: 'failed',
          error: error.message,
          failedAt: Timestamp.now()
        });
      }
    } finally {
      setLoading(false);
      setPendingSMS(null);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getRecipientCount = () => {
    return selectedUsers.length;
  };

  const getRecipientSummary = () => {
    if (selectedUsers.length === 0) return 'No recipients selected';
    if (selectedUsers.length === filteredUsers.length) {
      if (selectedYearGroup === 'all') {
        return `All ${selectedUsers.length} users`;
      } else {
        const group = yearGroups.get(selectedYearGroup);
        return `All ${selectedUsers.length} ${group?.displayName || 'members'}`;
      }
    }
    return `${selectedUsers.length} of ${filteredUsers.length} selected`;
  };

  const getGroupStats = () => {
    const stats = [];
    for (const [prefix, group] of yearGroups.entries()) {
      stats.push({
        prefix,
        displayName: group.displayName,
        count: group.count
      });
    }
    return stats.sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  const getValidPhoneCount = () => {
    const selectedUsersDetails = users.filter(user => selectedUsers.includes(user.id));
    return selectedUsersDetails.filter(user => validatePhoneNumber(user.phone)).length;
  };

  const testGatewayConnection = async () => {
    setLoading(true);
    try {
      await fetchGatewayStatus();
      if (gatewayStatus?.connected) {
        showSnackbar('✅ Gateway connected successfully!', 'success');
      } else {
        showSnackbar('❌ Gateway is not responding. Check URL and token.', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to connect to gateway', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#1034A6', fontWeight: 'bold', mb: 3 }}>
        <SmsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        SMS Communication (Android Gateway)
      </Typography>
      
      {/* Gateway Status Banner */}
      {gatewayStatus && (
        <Alert 
          severity={gatewayStatus.connected ? "success" : "error"}
          icon={gatewayStatus.connected ? <SignalIcon /> : <WarningIcon />}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <strong>Android SMS Gateway:</strong> {gatewayStatus.connected ? '✅ Connected' : '❌ Offline'}
              {gatewayStatus.connected ? (
                <>
                  <br />
                  <small>
                    SIM: {gatewayStatus.simNumber} | 
                    Signal: {gatewayStatus.signalStrength}% | 
                    Balance: {gatewayStatus.balance} SMS
                    {gatewayStatus.deviceId && ` | Device: ${gatewayStatus.deviceId}`}
                  </small>
                </>
              ) : (
                <>
                  <br />
                  <small>Check gateway URL and private token in settings</small>
                </>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={testGatewayConnection}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : 'Test Connection'}
              </Button>
              <IconButton size="small" onClick={fetchGatewayStatus}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Alert>
      )}
      
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<SendIcon />} label="Send SMS" />
        <Tab icon={<HistoryIcon />} label="History" />
        <Tab icon={<SimIcon />} label="Gateway Settings" />
      </Tabs>
      
      {/* Send SMS Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Left Column - Message Composition */}
          <Grid item xs={12} md={7}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmsIcon /> Compose SMS
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={3}>
                  {/* Message Input */}
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="SMS Message"
                    value={message}
                    onChange={handleMessageChange}
                    placeholder="Type your SMS message here..."
                    helperText={`${messageLength} characters • ${messageParts} message part(s)`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SmsIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  {/* SMS Info */}
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom>SMS Information</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip 
                        icon={<SmsIcon />}
                        label={`${messageParts} part(s)`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      <Chip 
                        icon={<PeopleIcon />}
                        label={`Valid recipients: ${getValidPhoneCount()}`} 
                        size="small" 
                        variant="outlined"
                      />
                      <Chip 
                        icon={<SpeedIcon />}
                        label={`Est. cost: KES ${(getValidPhoneCount() * messageParts * 0.5).toFixed(2)}`} 
                        size="small" 
                        color="warning"
                        variant="outlined"
                      />
                    </Stack>
                  </Paper>
                  
                  {/* Schedule Options */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isScheduled}
                          onChange={(e) => setIsScheduled(e.target.checked)}
                        />
                      }
                      label="Schedule for later"
                    />
                    
                    {isScheduled && (
                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <TextField
                          type="date"
                          label="Date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                        <TextField
                          type="time"
                          label="Time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Stack>
                    )}
                  </Paper>
                  
                  {/* Character Counter */}
                  <LinearProgress 
                    variant="determinate" 
                    value={(messageLength / (messageParts === 1 ? 160 : messageParts * 160)) * 100} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Right Column - Recipient Selection */}
          <Grid item xs={12} md={5}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon /> Select Recipients
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  {/* Filter by Year Group */}
                  <FormControl fullWidth>
                    <InputLabel>Filter by Year Group</InputLabel>
                    <Select
                      value={selectedYearGroup}
                      label="Filter by Year Group"
                      onChange={handleYearGroupChange}
                    >
                      <MenuItem value="all">
                        <em>All Year Groups ({users.length} users)</em>
                      </MenuItem>
                      {getGroupStats().map((group) => (
                        <MenuItem key={group.prefix} value={group.prefix}>
                          {group.displayName} ({group.count} users)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Recipient Summary */}
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Selected Recipients
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {getRecipientCount()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {getRecipientSummary()}
                        </Typography>
                        <Typography variant="caption" display="block" color="success.main">
                          {getValidPhoneCount()} have valid phone numbers
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={handleSelectAll}
                        size="small"
                      >
                        {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </Stack>
                  </Paper>
                  
                  {/* Users List */}
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Users ({filteredUsers.length})
                    </Typography>
                    <Stack spacing={1}>
                      {filteredUsers.map((user) => (
                        <Paper
                          key={user.id}
                          variant="outlined"
                          sx={{
                            p: 1,
                            cursor: 'pointer',
                            bgcolor: selectedUsers.includes(user.id) ? '#e3f2fd' : 'white',
                            '&:hover': { bgcolor: '#f5f5f5' }
                          }}
                          onClick={() => handleUserToggle(user.id)}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {user.fullName || user.firstName || user.email}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {validatePhoneNumber(user.phone) ? (
                                  <><PhoneIcon fontSize="inherit" sx={{ color: '#25D366' }} /> {user.phone}</>
                                ) : (
                                  <span style={{ color: '#f44336' }}>No valid phone</span>
                                )}
                              </Typography>
                            </Box>
                            {selectedUsers.includes(user.id) && (
                              <CheckCircleIcon color="primary" fontSize="small" />
                            )}
                          </Stack>
                        </Paper>
                      ))}
                      {filteredUsers.length === 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                          No users found
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  
                  {/* Send Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={prepareSMSBatch}
                    disabled={loading || selectedUsers.length === 0 || !message.trim() || getValidPhoneCount() === 0 || !gatewayStatus?.connected}
                    sx={{
                      mt: 2,
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#128C7E' }
                    }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  >
                    {loading ? 'Sending...' : `Send to ${getValidPhoneCount()} Recipients`}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* History Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> SMS Sending History
              <IconButton size="small" onClick={fetchSendingHistory}>
                <RefreshIcon />
              </IconButton>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              {sendingHistory.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No SMS history found
                </Typography>
              ) : (
                sendingHistory.map((history) => (
                  <Paper key={history.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          To: {history.recipientCount} recipients
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {history.createdAt?.toDate().toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {history.message?.substring(0, 100)}...
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Delivered">
                          <Chip 
                            icon={<DeliveredIcon />}
                            label={history.deliveredCount || 0}
                            size="small"
                            color="success"
                          />
                        </Tooltip>
                        <Tooltip title="Failed">
                          <Chip 
                            icon={<FailedIcon />}
                            label={history.failedCount || 0}
                            size="small"
                            color="error"
                          />
                        </Tooltip>
                        <Tooltip title="Message Parts">
                          <Chip 
                            icon={<SmsIcon />}
                            label={`${history.messageParts || 1} part(s)`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
      
      {/* Settings Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SimIcon /> Android Gateway Configuration
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Android SMS Gateway Setup:</strong>
                  <ol style={{ marginTop: 8, marginBottom: 0 }}>
                    <li>Install SMS Gateway app on Android device</li>
                    <li>Open the app and note the API URL and Private Token</li>
                    <li>Insert SIM card with SMS bundle</li>
                    <li>Device must remain powered on and connected to the same network</li>
                    <li>Private token is used for authentication (ignored in public server)</li>
                  </ol>
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gateway API URL"
                  value={smsSettings.gatewayUrl}
                  onChange={(e) => setSmsSettings({...smsSettings, gatewayUrl: e.target.value})}
                  helperText="URL provided by your SMS Gateway APK (e.g., http://192.168.1.100:8080)"
                  placeholder="http://192.168.1.100:8080"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Private Token"
                  type={showPrivateToken ? 'text' : 'password'}
                  value={smsSettings.privateToken}
                  onChange={(e) => setSmsSettings({...smsSettings, privateToken: e.target.value})}
                  helperText="Token provided by your SMS Gateway APK (used for authentication)"
                  placeholder="Enter your private token"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPrivateToken(!showPrivateToken)}
                          edge="end"
                        >
                          {showPrivateToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Sender ID"
                  value={smsSettings.senderId}
                  onChange={(e) => setSmsSettings({...smsSettings, senderId: e.target.value})}
                  helperText="WhiteDoves (max 11 chars)"
                  placeholder="WhiteDoves"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max per Batch"
                  value={smsSettings.maxPerBatch}
                  onChange={(e) => setSmsSettings({...smsSettings, maxPerBatch: parseInt(e.target.value) || 20})}
                  helperText="Maximum recipients per batch"
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Retry Count"
                  value={smsSettings.retryCount}
                  onChange={(e) => setSmsSettings({...smsSettings, retryCount: parseInt(e.target.value) || 3})}
                  helperText="Number of retry attempts on failure"
                  inputProps={{ min: 0, max: 10 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (seconds)"
                  value={smsSettings.timeoutSeconds}
                  onChange={(e) => setSmsSettings({...smsSettings, timeoutSeconds: parseInt(e.target.value) || 30})}
                  helperText="Request timeout in seconds"
                  inputProps={{ min: 5, max: 120 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={smsSettings.enableDeliveryReport}
                      onChange={(e) => setSmsSettings({...smsSettings, enableDeliveryReport: e.target.checked})}
                    />
                  }
                  label="Enable Delivery Reports"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        // Validate URL
                        if (!smsSettings.gatewayUrl) {
                          showSnackbar('Please enter a gateway URL', 'error');
                          return;
                        }
                        
                        await updateDoc(doc(db, 'settings', 'smsSettings'), smsSettings);
                        showSnackbar('✅ Settings saved successfully!', 'success');
                        
                        // Test connection after saving
                        await fetchGatewayStatus();
                      } catch (error) {
                        console.error('Error saving settings:', error);
                        showSnackbar('Failed to save settings: ' + error.message, 'error');
                      }
                    }}
                  >
                    Save Settings
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={testGatewayConnection}
                    disabled={loading || !smsSettings.gatewayUrl}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Test Connection'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm SMS Batch</DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemIcon><PeopleIcon /></ListItemIcon>
              <ListItemText 
                primary="Recipients" 
                secondary={`${pendingSMS?.totalRecipients} people will receive this SMS`}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SmsIcon /></ListItemIcon>
              <ListItemText 
                primary="Message Parts" 
                secondary={`${pendingSMS?.messageParts} SMS part(s) per recipient`}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CreditsIcon /></ListItemIcon>
              <ListItemText 
                primary="Estimated Cost" 
                secondary={`KES ${pendingSMS?.estimatedCost?.toFixed(2)}`}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ScheduleIcon /></ListItemIcon>
              <ListItemText 
                primary="Schedule" 
                secondary={pendingSMS?.scheduledFor ? new Date(pendingSMS.scheduledFor).toLocaleString() : 'Send now'}
              />
            </ListItem>
          </List>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Please confirm:</strong> This will send {pendingSMS?.totalRecipients} SMS messages using your Android gateway.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={sendSMS} variant="contained" color="primary">
            Send Now
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SMSCommunication;
